import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { UploadField } from "@/components/UploadField";
import { CityInput } from "@/components/CityInput";
import { LibraryCover } from "@/components/LibraryCover";

const UFS = ["AC", "AL", "AM", "AP", "BA", "CE", "DF", "ES", "GO", "MA", "MG", "MS", "MT", "PA", "PB", "PE", "PI", "PR", "RJ", "RN", "RO", "RR", "RS", "SC", "SE", "SP", "TO"];
const PRES = { modality: "ead", program: "", starts_at: "", city: "", state: "", venue: "", address: "", event_date: "", event_end: "", event_time: "", seats: "", bonus_courses: [] };
const EMPTY = { ...PRES, title: "", description: "", category: "Online", duration: "", banner: "", price: "", promo_price: "", published: true, certificate_enabled: true, order: "", coordinator_id: "none", access_months: "0", custom_months: "" };

/** Criar (course = null) ou editar (course = objeto) um curso. */
export const CourseFormDialog = ({ open, onOpenChange, course, onSaved }) => {
  const editing = !!course;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [instructors, setInstructors] = useState([]);
  const [allCourses, setAllCourses] = useState([]);

  useEffect(() => {
    if (!open) return;
    setForm(course ? {
      title: course.title || "", description: course.description || "", category: course.category || "",
      duration: course.duration || "", banner: course.banner || "", price: course.price ? String(course.price) : "", promo_price: course.promo_price ? String(course.promo_price) : "",
      published: course.published !== false, certificate_enabled: course.certificate_enabled !== false,
      order: course.order ? String(course.order) : "", coordinator_id: course.coordinator_id || "none",
      modality: course.modality === "presencial" ? "presencial" : "ead", starts_at: course.starts_at || "", city: course.city || "", state: course.state || "",
      venue: course.venue || "", address: course.address || "", event_date: course.event_date || "", event_end: course.event_end || "",
      event_time: course.event_time || "", seats: course.seats ? String(course.seats) : "", program: course.program || "",
      bonus_courses: (course.bonus_courses || []).map((b) => ({ course_id: b.course_id, fee: b.fee ? String(b.fee) : "" })),
      ...(["0", "3", "6", "12"].includes(String(course.access_months || 0))
        ? { access_months: String(course.access_months || 0), custom_months: "" }
        : { access_months: "outro", custom_months: String(course.access_months) }),
    } : EMPTY);
    api.get("/instructors").then((r) => setInstructors(r.data)).catch(() => setInstructors([]));
    api.get("/courses").then((r) => setAllCourses(r.data)).catch(() => setAllCourses([]));
  }, [open, course]);

  const up = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Informe o título do curso.");
    const pres = form.modality === "presencial";
    if (pres && (!form.city.trim() || !form.state || !form.event_date)) return toast.error("Curso presencial: informe cidade, UF e a data de início.");
    if (pres && form.event_end && form.event_end < form.event_date) return toast.error("A data de fim não pode ser antes da data de início.");
    setSaving(true);
    try {
      const body = {
        ...form,
        price: parseFloat(String(form.price).replace(",", ".")) || 0,
        promo_price: parseFloat(String(form.promo_price || "").replace(",", ".")) || 0,
        order: parseInt(form.order, 10) || 0,
        coordinator_id: form.coordinator_id === "none" ? "" : form.coordinator_id,
        access_months: form.access_months === "outro" ? (parseInt(form.custom_months, 10) || 0) : parseInt(form.access_months, 10) || 0,
        seats: parseInt(form.seats, 10) || 0,
        ...(pres ? { certificate_enabled: false, access_months: 0 } : {}),
        bonus_courses: pres ? form.bonus_courses.map((b) => ({ course_id: b.course_id, fee: parseFloat(String(b.fee).replace(",", ".")) || 0 })) : [],
      };
      delete body.custom_months;
      const res = editing ? await api.put(`/courses/${course.id}`, body) : await api.post("/courses", body);
      toast.success(editing ? "Curso atualizado!" : "Curso criado com sucesso!");
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto iep-scroll">
        <DialogHeader><DialogTitle className="font-display">{editing ? "Editar curso" : "Novo curso"}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Título *</Label>
            <Input data-testid="course-title-input" value={form.title} onChange={up("title")} placeholder="Ex.: Gestão Empresarial" />
          </div>
          <div className="space-y-1.5">
            <Label>Descrição</Label>
            <Textarea data-testid="course-description-input" value={form.description} onChange={up("description")} rows={3} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Categoria *</Label>
              <select value={form.category} data-testid="course-category-select" className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                onChange={(e) => { const v = e.target.value; setForm((p) => ({ ...p, category: v, modality: v === "Presencial" ? "presencial" : "ead" })); }}>
                <option value="Online">Online</option>
                <option value="Presencial">Presencial</option>
                {form.category && !["Online", "Presencial"].includes(form.category) && <option value={form.category}>{form.category}</option>}
              </select></div>
            <div className="space-y-1.5"><Label>Carga horária</Label><Input value={form.duration} onChange={up("duration")} placeholder="12h" /></div>
          </div>
          {form.modality !== "presencial" && (
            <div className="space-y-1.5"><Label>Início das aulas (opcional)</Label>
              <Input type="datetime-local" value={form.starts_at} onChange={up("starts_at")} />
              <p className="text-xs text-slate-400">Antes dessa data, a aluna vê o curso mas as aulas ficam fechadas.</p></div>
          )}
          {form.modality === "presencial" && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-3 space-y-3" data-testid="new-course-presencial">
              <div className="text-sm font-semibold text-slate-800">Turma presencial</div>
              <div className="grid grid-cols-[1fr_80px] gap-3">
                <div className="space-y-1.5"><Label>Cidade *</Label><CityInput value={form.city} onChange={(v) => setForm((p) => ({ ...p, city: v }))} onPick={(o) => setForm((p) => ({ ...p, city: o.city, state: o.state }))} placeholder="Ex.: Recife" testid="new-course-city" /></div>
                <div className="space-y-1.5"><Label>UF *</Label>
                  <select value={form.state} onChange={up("state")} className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"><option value="">—</option>{UFS.map((u) => <option key={u} value={u}>{u}</option>)}</select></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Data de início *</Label><Input type="date" value={form.event_date} onChange={up("event_date")} data-testid="new-course-start" /></div>
                <div className="space-y-1.5"><Label>Data de fim</Label><Input type="date" value={form.event_end} min={form.event_date || undefined} onChange={up("event_end")} data-testid="new-course-end" /></div>
              </div>
              <div className="grid grid-cols-[1fr_100px] gap-3">
                <div className="space-y-1.5"><Label>Horário</Label><Input value={form.event_time} onChange={up("event_time")} placeholder="9h às 18h" /></div>
                <div className="space-y-1.5"><Label>Vagas</Label><Input type="number" min="0" value={form.seats} onChange={up("seats")} placeholder="Livre" /></div>
              </div>
              <div className="space-y-1.5"><Label>Local</Label><Input value={form.venue} onChange={up("venue")} placeholder="Ex.: Salão Beleza Pura" /></div>
              <div className="space-y-1.5"><Label>Endereço</Label><Input value={form.address} onChange={up("address")} placeholder="Rua, número, bairro" /></div>
              <div className="space-y-1.5"><Label>Grade do curso / programação</Label>
                <Textarea rows={5} value={form.program} onChange={up("program")} placeholder={"Ex.:\n1º dia – Química capilar e pH\n2º dia – Colorimetria na prática\n..."} data-testid="new-course-program" />
                <p className="text-xs text-slate-400">Curso presencial não precisa de aulas na plataforma: a grade aparece na página de venda e na área da aluna.</p></div>
              <div className="space-y-1.5">
                <Label>Curso bônus (a aluna ganha ao pagar)</Label>
                {allCourses.filter((c) => c.modality !== "presencial" && c.id !== course?.id).map((c) => {
                  const b = form.bonus_courses.find((x) => x.course_id === c.id);
                  return (
                    <div key={c.id} className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2 py-1.5">
                      <Switch checked={!!b} onCheckedChange={(v) => setForm((p) => ({ ...p, bonus_courses: v ? [...p.bonus_courses, { course_id: c.id, fee: "" }] : p.bonus_courses.filter((x) => x.course_id !== c.id) }))} />
                      <span className="flex-1 text-sm truncate">{c.title}</span>
                      {b && <Input value={b.fee} placeholder="Taxa R$" title="Taxa da franqueada por aluna (venda da CEO não gera taxa)" className="w-24 h-8"
                        onChange={(e) => setForm((p) => ({ ...p, bonus_courses: p.bonus_courses.map((x) => x.course_id === c.id ? { ...x, fee: e.target.value } : x) }))} />}
                    </div>
                  );
                })}
                <p className="text-xs text-slate-500">A aluna entra na turma de quem vendeu. A taxa só é lançada quando quem vendeu é professora franqueada.</p>
              </div>
            </div>
          )}
          <div className="grid grid-cols-[1fr_2fr] gap-4">
            <div className="space-y-1.5">
              <Label>Ordem</Label>
              <Input type="number" min="0" value={form.order} onChange={up("order")} placeholder="1" data-testid="course-order-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Coordenador(a)</Label>
              <Select value={form.coordinator_id} onValueChange={(v) => setForm((p) => ({ ...p, coordinator_id: v }))}>
                <SelectTrigger data-testid="course-coordinator-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem coordenador(a)</SelectItem>
                  {instructors.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.modality !== "presencial" && <div className="space-y-1.5">
            <Label>Prazo de acesso (bônus de matrícula curso presencial)</Label>
            <div className="flex gap-2">
              <select value={form.access_months} onChange={up("access_months")} className="h-10 flex-1 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="course-access-months">
                <option value="0">Sem prazo</option>
                <option value="3">3 meses</option>
                <option value="6">6 meses</option>
                <option value="12">1 ano</option>
                <option value="outro">Outro (meses)</option>
              </select>
              {form.access_months === "outro" && (
                <Input type="number" min="1" max="120" value={form.custom_months} onChange={up("custom_months")} placeholder="meses" className="w-28" />
              )}
            </div>
            <p className="text-xs text-slate-400">Usado quando o aluno é cadastrado com o bônus: o acesso vence nesse prazo, a partir do dia do cadastro.</p>
          </div>}
          <div className="space-y-1.5">
            <Label>Valor no cartão — valor real (R$) — deixe 0 para curso gratuito</Label>
            <Input data-testid="course-price-input" type="number" min="0" step="0.01" value={form.price} onChange={up("price")} placeholder="0,00" />
          </div>
          <div className="space-y-1.5">
            <Label>Valor no Pix — com desconto (R$) — opcional</Label>
            <Input data-testid="course-promo-input" type="number" min="0" step="0.01" value={form.promo_price || ""} onChange={up("promo_price")} placeholder="Sem desconto" />
            <p className="text-[11px] text-slate-400">Vale só no Pix. O valor acima (cartão) aparece riscado ao lado.</p>
          </div>
          <div className="space-y-1.5">
            <Label>Capa do curso (proporção 3:2, ex.: 1536×1024)</Label>
            <div className="flex gap-2">
              <Input value={form.banner} onChange={up("banner")} placeholder="https://... ou envie" />
              <UploadField testid="course-banner-upload" onUploaded={(url) => setForm((p) => ({ ...p, banner: url }))} />
            </div>
            {form.banner && <img src={form.banner} alt="" className="mt-1 w-48 aspect-[3/2] object-cover rounded-lg border border-slate-200" />}
            <LibraryCover city={form.modality === "presencial" ? form.city : ""} onPick={(url) => setForm((p) => ({ ...p, banner: url }))} />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
            <Label className="cursor-pointer">Publicado (visível aos alunos)</Label>
            <Switch checked={form.published} onCheckedChange={(v) => setForm((p) => ({ ...p, published: v }))} />
          </div>
          {form.modality !== "presencial" && <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
            <div>
              <Label className="cursor-pointer">Emitir certificado ao concluir</Label>
              <p className="text-xs text-slate-400">Se desligado, este curso não gera certificado.</p>
            </div>
            <Switch data-testid="course-certificate-toggle" checked={form.certificate_enabled} onCheckedChange={(v) => setForm((p) => ({ ...p, certificate_enabled: v }))} />
          </div>}
          {form.modality === "presencial" && <p className="text-xs text-slate-500 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">No presencial o certificado é entregue em sala de aula, e o acesso online é o do curso bônus (com o prazo dele).</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button data-testid="course-save-button" onClick={save} disabled={saving} className="text-white" style={{ background: "#1E9E2A" }}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Salvar alterações" : "Criar curso"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
